import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Globe2, Loader2, ShieldCheck } from "lucide-react";
import { inputCls } from "@/lib/studioApi";
import { CLIENT_TYPES, TREATMENT_LABELS, decideFiscal, useFiscal } from "@/lib/fiscal";

/* Analyse TVA d'un devis / d'une facture : pays et type de client, traitement proposé, confirmation explicite de l'artisan. */
export const FiscalPanel = ({ value, onChange, readOnly = false, compact = false }) => {
  const fiscal = useFiscal();
  const [decision, setDecision] = useState(value?.fiscal || null);
  const [busy, setBusy] = useState(false);
  const { client_country = "", client_type = "", client_vat = "", fiscal_confirmed = false } = value || {};

  useEffect(() => {
    if (!fiscal) return;
    let alive = true;
    setBusy(true);
    const t = setTimeout(() => {
      decideFiscal({ client_country, client_type, client_vat, confirmed: fiscal_confirmed }).then((d) => alive && setDecision(d)).catch(() => {}).finally(() => alive && setBusy(false));
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [fiscal, client_country, client_type, client_vat, fiscal_confirmed]);

  if (!fiscal) return null;
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value, ...(k !== "fiscal_confirmed" ? { fiscal_confirmed: false } : {}) });
  const blocking = decision?.blocking;
  const tone = blocking ? "border-amber-300 bg-amber-50" : decision?.requires_confirmation ? "border-emerald-200 bg-emerald-50/60" : "border-[#E2E7F7] bg-white";

  return (
    <section data-testid="fiscal-panel" className={`rounded-2xl border p-4 ${tone}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="inline-flex items-center gap-2 text-sm font-bold text-[#0F2645]"><Globe2 className="h-4 w-4 text-[#3A5CC8]" /> TVA & fiscalité du client <span className="rounded-full bg-white/80 px-2 py-0.5 text-[11px] font-semibold text-[#5C6A87]">Artisan : {fiscal.country} · {fiscal.currency}</span></p>
        {busy ? <Loader2 className="h-4 w-4 animate-spin text-[#93A0BC]" /> : decision && (
          <span data-testid="fiscal-treatment" className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold ${blocking ? "bg-amber-100 text-amber-900" : "bg-[#0F2645] text-white"}`}>
            {blocking ? <AlertTriangle className="h-3.5 w-3.5" /> : <CheckCircle2 className="h-3.5 w-3.5" />} {TREATMENT_LABELS[decision.treatment] || decision.treatment}
          </span>
        )}
      </div>
      {!readOnly && (
        <div className={`mt-3 grid gap-2 ${compact ? "sm:grid-cols-3" : "sm:grid-cols-3"}`}>
          <label className="block"><span className="mb-1 block text-[11px] font-semibold text-[#5C6A87]">Pays du client</span>
            <select data-testid="fiscal-client-country" className={inputCls} value={client_country} onChange={set("client_country")}>
              <option value="">— non précisé ({fiscal.country} supposé)</option>
              {(fiscal.countries || []).map((c) => <option key={c.country} value={c.country}>{c.flag} {c.name}</option>)}
              {["NL", "DE", "IT", "PT", "CH", "GB", "US"].map((c) => <option key={c} value={c}>{c}</option>)}
            </select></label>
          <label className="block"><span className="mb-1 block text-[11px] font-semibold text-[#5C6A87]">Type de client</span>
            <select data-testid="fiscal-client-type" className={inputCls} value={client_type} onChange={set("client_type")}>
              <option value="">— à préciser</option>
              {CLIENT_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select></label>
          <label className="block"><span className="mb-1 block text-[11px] font-semibold text-[#5C6A87]">N° TVA / identifiant fiscal du client</span>
            <input data-testid="fiscal-client-vat" className={`${inputCls} font-mono`} placeholder={client_country === "MA" ? "ICE" : client_country === "ES" ? "ESB12345678" : "BE0123456789"} value={client_vat} onChange={set("client_vat")} /></label>
        </div>
      )}
      {decision?.warnings?.length > 0 && (
        <ul data-testid="fiscal-warnings" className="mt-3 space-y-1 text-xs leading-relaxed text-[#5C6A87]">
          {decision.warnings.map((w, i) => <li key={i} className="flex gap-2"><span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-[#3A5CC8]" />{w}</li>)}
        </ul>
      )}
      {decision?.mention && <p className="mt-2 rounded-lg bg-white/80 p-2 text-[11px] italic text-[#0F2645]">Mention sur le document : « {decision.mention} »</p>}
      {!readOnly && decision?.requires_confirmation && decision.treatment !== "needs_info" && (
        <label data-testid="fiscal-confirm" className="mt-3 flex cursor-pointer items-start gap-2 rounded-xl border border-[#C9D4F4] bg-white p-3 text-sm text-[#0F2645]">
          <input type="checkbox" className="mt-0.5 h-4 w-4 accent-[#3A5CC8]" checked={!!fiscal_confirmed} onChange={(e) => onChange({ ...value, fiscal_confirmed: e.target.checked })} />
          <span><ShieldCheck className="mr-1 inline h-4 w-4 text-[#3A5CC8]" /><strong>Je confirme ce traitement TVA</strong> pour ce document (vérifié avec mon comptable si nécessaire). Sans confirmation, l'envoi est bloqué.</span>
        </label>
      )}
      {decision?.treatment === "needs_info" && <p data-testid="fiscal-missing" className="mt-2 text-xs font-semibold text-amber-900">À compléter : {(decision.missing || []).join(", ")}</p>}
    </section>
  );
};
